import React, { useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import {
    ScrollArea,
    Stack,
    Table,
    Text,
    Badge,
    Group,
    Alert,
    ActionIcon,
    Tooltip,
    Box,
} from '@mantine/core';
import { IconEye, IconArrowRight, IconTrash, IconAlertTriangle, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useGetSavedMMECalculations } from '../../../global/entities/MME/queries/useGetSavedMMECalculations.tsx';
import { useDeleteSavedMMECalculation } from '../../../global/entities/MME/mutations/useDeleteSavedMMECalculation.tsx';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { ISavedMMECalculation } from '../../../global/entities/MME/MME.ts';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { modals } from '@mantine/modals';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useUserConsent } from '../../../global/hooks/useUserConsent.ts';
import { MMEWarningModal } from '../../../global/component/organism/MMEWarningModal/MMEWarningModal.tsx';

const SavedMMECalculationsTable: React.FC<{ patientStore?: PatientStore }> = inject('patientStore')(
    observer(({ patientStore }) => {
        const { t } = useTranslation();
        const patientID = patientStore?.patientId ? parseInt(patientStore.patientId) : undefined;
        const { data: savedCalculations, isLoading } = useGetSavedMMECalculations(patientID);
        const { sortedData, setSortedData, sortBy, reverseSortDirection, setSorting } = useTable();
        const navigate = useNavigate();
        const deleteMutation = useDeleteSavedMMECalculation(patientID);
        const { hasAcceptedMMEWarning, acceptMMEWarning, isLoading: isLoadingConsent } = useUserConsent();

        useEffect(() => {
            if (savedCalculations?.length) 
                setSortedData(savedCalculations);
             else 
                setSortedData([]);
            
        }, [savedCalculations, setSortedData]);

        const getRiskLevelColor = (riskLevel: string) => {
            switch (riskLevel?.toLowerCase()) {
                case 'low':
                    return 'green';
                case 'moderate':
                    return 'yellow';
                case 'high':
                    return 'red';
                default:
                    return 'gray';
            }
        };

        const getRiskLevelLabel = (riskLevel: string) => {
            switch (riskLevel) {
                case 'Low':
                    return t('mme.riskLevel.low', 'Faible');
                case 'Moderate':
                    return t('mme.riskLevel.moderate', 'Modéré');
                case 'High':
                    return t('mme.riskLevel.high', 'Élevé');
                default:
                    return riskLevel;
            }
        };

        const formatDate = (dateString: string) => {
            try {
                const date = new Date(dateString);
                return date.toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            } catch {
                return dateString;
            }
        };

        const showCalculationDetails = (calculation: ISavedMMECalculation) => {
            modals.open({
                title: t('mme.savedCalculations.details', 'Détails du calcul'),
                children: (
                    <Stack gap="md">
                        <Group justify="space-between">
                            <Text fw={600} size="lg">
                                {calculation.totalDailyMME.toFixed(2)} MME/jour
                            </Text>
                            <Badge color={getRiskLevelColor(calculation.riskLevel)} variant="light">
                                {getRiskLevelLabel(calculation.riskLevel)}
                            </Badge>
                        </Group>

                        <Group gap="xs">
                            <Text size="sm" c="dimmed">
                                {t('mme.savedCalculations.hcp', 'HCP')}: {calculation.healthcareProfessionalName}
                            </Text>
                            <Text size="sm" c="dimmed">|</Text>
                            <Text size="sm" c="dimmed">
                                {t('mme.savedCalculations.referenceVersion', 'Version de référence')}: {calculation.conversionDataVersion?.versionName || calculation.referenceVersion}
                            </Text>
                        </Group>

                        {calculation.riskLevel?.toLowerCase() !== 'low' && calculation.riskLevel?.toLowerCase() !== 'none' && calculation.riskMessage && (
                            <Alert
                                icon={
                                    calculation.riskLevel?.toLowerCase() === 'high'
                                        ? <IconAlertCircle size={16} />
                                        : <IconAlertTriangle size={16} />
                                }
                                title={getRiskLevelLabel(calculation.riskLevel)}
                                color={calculation.riskLevel?.toLowerCase() === 'high' ? 'red' : 'orange'}
                            >
                                <Text size="sm">
                                    {calculation.riskMessage}
                                        </Text>
                            </Alert>
                        )}

                        <Stack gap="xs">
                            <Text size="sm" fw={500}>
                                {t('mme.savedCalculations.details', 'Détails du calcul')}:
                            </Text>
                            {calculation.calculationDetails && calculation.calculationDetails.length > 0 ? (
                                calculation.calculationDetails.map((calc, index) => (
                                    <Box key={index} p="xs" style={{ backgroundColor: calc.hasWarning ? '#fff3cd' : '#f8f9fa' }}>
                                        <Group justify="space-between">
                                            <Text size="sm" fw={500}>
                                                {calc.drugName}
                                            </Text>
                                            <Badge color={calc.hasWarning ? 'yellow' : 'green'} variant="light" size="sm">
                                                {calc.dailyMME.toFixed(2)} MME/jour
                                            </Badge>
                                        </Group>
                                        <Text size="xs" c="dimmed">
                                            {calc.dose} × {calc.frequencyPerDay}x/jour × {calc.conversionFactor.toFixed(4)} = {calc.dailyMME.toFixed(2)} MME
                                        </Text>
                                        {calc.hasWarning && calc.warningMessage && (
                                            <Alert icon={<IconAlertTriangle size={14} />} color="yellow" mt="xs" p="xs">
                                                <Text size="xs">{calc.warningMessage}</Text>
                                            </Alert>
                                        )}
                                    </Box>
                                ))
                            ) : (
                                <Text size="sm" c="dimmed">
                                    {t('mme.savedCalculations.noDetails', 'Aucun détail disponible')}
                                </Text>
                            )}
                        </Stack>
                    </Stack>
                ),
                size: 'lg',
            });
        };

        const reopenCalculation = (calculation: ISavedMMECalculation) => {
            navigate('/hcpPatientDashboard/dosageModeling/mmeCalculator', {
                state: {
                    mmeReopen: {
                        savedCalculationId: calculation.id,
                        inputData: calculation.inputData,
                        canBeDeleted: calculation.canBeDeleted,
                        conversionDataVersionID: calculation.conversionDataVersionID,
                    },
                },
            });
        };

        const handleDeleteSuccess = () => {
            notifications.show({
                title: t('mme.savedCalculations.deleteSuccess', 'Calcul supprimé'),
                message: t('mme.savedCalculations.deleteSuccessMessage', 'Le calcul MME a été supprimé avec succès'),
                color: 'green',
            });
        };

        const handleDeleteError = (error: any) => {
            notifications.show({
                title: t('mme.savedCalculations.deleteError', 'Erreur'),
                message: error?.message || t('mme.savedCalculations.deleteErrorMessage', 'Une erreur est survenue lors de la suppression'),
                color: 'red',
            });
        };

        const confirmDelete = (calculation: ISavedMMECalculation) => {
            modals.openConfirmModal({
                title: t('mme.savedCalculations.deleteTitle', 'Supprimer le calcul'),
                children: (
                    <Text size="sm">
                        {t('mme.savedCalculations.deleteConfirmation', 'Êtes-vous sûr de vouloir supprimer ce calcul MME? Cette action est irréversible.')}
                    </Text>
                ),
                labels: {
                    confirm: t('mme.savedCalculations.deleteConfirm', 'Supprimer'),
                    cancel: t('mme.savedCalculations.deleteCancel', 'Annuler'),
                },
                confirmProps: { color: 'red' },
                onConfirm: () => {
                    deleteMutation.mutate(calculation.id, {
                        onSuccess: handleDeleteSuccess,
                        onError: handleDeleteError,
                    });
                },
            });
        };

        if (isLoading) {
            return (
                <Text size="sm" c="dimmed">
                    {t('mme.savedCalculations.loading', 'Chargement...')}
                </Text>
            );
        }

        return (
            <>
                <MMEWarningModal isOpen={!hasAcceptedMMEWarning && !isLoadingConsent} onAccepted={acceptMMEWarning} />
                <ScrollArea w="100%" style={{ pointerEvents: hasAcceptedMMEWarning ? 'auto' : 'none', opacity: hasAcceptedMMEWarning ? 1 : 0.5 }}>
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Th
                                sorted={sortBy === 'calculationDate'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('calculationDate', sortedData)}
                            >
                                {t('mme.savedCalculations.date', 'Date')}
                            </Th>
                            <Th
                                sorted={sortBy === 'totalDailyMME'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('totalDailyMME', sortedData)}
                            >
                                {t('mme.savedCalculations.totalMME', 'MME Total')}
                            </Th>
                            <Th
                                sorted={sortBy === 'riskLevel'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('riskLevel', sortedData)}
                            >
                                {t('mme.savedCalculations.riskLevel', 'Niveau de risque')}
                            </Th>
                            <Th
                                sorted={sortBy === 'healthcareProfessionalName'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('healthcareProfessionalName', sortedData)}
                            >
                                {t('mme.savedCalculations.hcp', 'HCP')}
                            </Th>
                            <Table.Th>
                                {t('mme.savedCalculations.referenceVersion', 'Version de référence')}
                            </Table.Th>
                            <Table.Th style={{ position: 'sticky', right: 0, backgroundColor: 'white', zIndex: 1 }}>
                                {t('mme.savedCalculations.actions', 'Actions')}
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {sortedData?.map((calculation: ISavedMMECalculation, index: number) => (
                            <Table.Tr
                                key={calculation.id}
                                style={{
                                    background:
                                        index % 2 === 1 ? 'var(--Background-Gray-light, #E9ECEF)' : undefined,
                                }}
                            >
                                <Table.Td>
                                    <Text size="sm">{formatDate(calculation.calculationDate)}</Text>
                                </Table.Td>
                                <Table.Td>
                                    <Text fw={600} size="sm">
                                        {calculation.totalDailyMME.toFixed(2)} {t('mme.calculator.dailyMME')}
                                    </Text>
                                </Table.Td>
                                <Table.Td>
                                    <Badge color={getRiskLevelColor(calculation.riskLevel)} variant="light" size="sm">
                                        {getRiskLevelLabel(calculation.riskLevel)}
                                    </Badge>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm">{calculation.healthcareProfessionalName}</Text>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm">
                                        {calculation.conversionDataVersion?.versionName}
                                    </Text>
                                </Table.Td>
                                <Table.Td
                                    style={{
                                        position: 'sticky',
                                        right: 0,
                                        backgroundColor: index % 2 === 1 ? 'var(--Background-Gray-light, #E9ECEF)' : 'white',
                                        zIndex: 1
                                    }}
                                >
                                    <Group gap="xs" wrap="nowrap">
                                        <Tooltip label={t('mme.savedCalculations.viewDetails', 'Voir les détails')}>
                                            <ActionIcon
                                                variant="subtle"
                                                color="blue"
                                                onClick={() => showCalculationDetails(calculation)}
                                            >
                                                <IconEye size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                        <Tooltip label={t('mme.savedCalculations.reopen', 'Ouvrir le calcul')}>
                                            <ActionIcon
                                                variant="transparent"
                                                onClick={() => reopenCalculation(calculation)}
                                            >
                                                <IconArrowRight size={18} />
                                            </ActionIcon>
                                        </Tooltip>
                                        <Tooltip 
                                            label={
                                                calculation.canBeDeleted 
                                                    ? t('mme.savedCalculations.delete', 'Supprimer')
                                                    : t('mme.savedCalculations.cannotDelete', 'Impossible de supprimer (associé à un plan de tapering)')
                                            }
                                        >
                                            <ActionIcon
                                                variant="subtle"
                                                color="red"
                                                onClick={() => confirmDelete(calculation)}
                                                loading={deleteMutation.isPending}
                                                disabled={!calculation.canBeDeleted}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
            </>
        );
    }),
);

const WrappedSavedMMECalculationsTable: React.FC = () => {
    return (
        <TableContextProvider>
            <SavedMMECalculationsTable />
        </TableContextProvider>
    );
};

export default WrappedSavedMMECalculationsTable;
